import { ChatMessage } from "@/entities/messaging/types";
import {
  isDateSeparatorMessage,
  isReservedMessage
} from "@/entities/messaging/helpers";

// A "real" message is an actual chat turn — not the transient WAITING_MSG
// spinner, the new-messages separator, or a date separator.
export const isRealChatMessage = (message: ChatMessage): boolean =>
  !isReservedMessage(message) && !isDateSeparatorMessage(message);

// Id of the last real message in a list, or "" if there is none. Used as the
// diff anchor instead of the raw last item, so the transient WAITING_MSG tail
// doesn't break the "what just arrived" comparison.
export const lastRealMessageId = (messages: ChatMessage[]): string => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isRealChatMessage(messages[i])) return messages[i].id;
  }
  return "";
};

// Ids of real messages appended to the tail since the previous list — i.e. those
// that follow the previous list's last real message — excluding any already
// animated. Empty for a first population, a reorder, or when the prior tail is
// gone, so only genuinely-new trailing messages get an entrance animation.
export const computeFreshMessageIds = (
  prevMessages: ChatMessage[],
  nextMessages: ChatMessage[],
  alreadyAnimated: ReadonlySet<string>
): Set<string> => {
  const freshMessageIds = new Set<string>();
  const prevLastRealId = lastRealMessageId(prevMessages);
  if (!prevLastRealId) return freshMessageIds;
  const anchorIndex = nextMessages.findIndex(
    (message) => message.id === prevLastRealId
  );
  if (anchorIndex === -1) return freshMessageIds;
  for (let i = anchorIndex + 1; i < nextMessages.length; i++) {
    const message = nextMessages[i];
    if (isRealChatMessage(message) && !alreadyAnimated.has(message.id)) {
      freshMessageIds.add(message.id);
    }
  }
  return freshMessageIds;
};
